import { Trips, TripsDetails } from "../fleet/trips/tripsApi";
import { EcoDrivingScore } from "./driversAPI";

const getTripTotalScore = (
  tripsDetail: TripsDetails[],
  key: keyof TripsDetails,
) => {
  return tripsDetail
    .filter((trip) => trip[key] !== null)
    .reduce(
      (acc, trip) => acc + (trip[key] as number) * (trip.secondsDuration ?? 0),
      0,
    );
};

const getDriverTripNumber = (
  trips: TripsDetails[],
  key: keyof TripsDetails,
) => {
  return trips
    .filter((trip) => !!trip[key])
    .reduce((acc, trip) => acc + (trip.secondsDuration ?? 0), 0);
};

export const drivingEventsCountsByDrivers = (trips: Trips[]) => {
  const result: Record<
    string,
    {
      harshAccelerationsCount: number;
      harshBrakesCount: number;
      sharpTurnsCount: number;
    }
  > = {};

  for (const trip of trips) {
    const driverUuid = trip.driver?.uuid;
    if (!driverUuid) continue;

    if (!result[driverUuid]) {
      result[driverUuid] = {
        harshAccelerationsCount: 0,
        sharpTurnsCount: 0,
        harshBrakesCount: 0,
      };
    }

    for (const tripDetail of trip.trips) {
      if (tripDetail.harshAccelerationsCount) {
        result[driverUuid].harshAccelerationsCount +=
          tripDetail.harshAccelerationsCount;
      }
      if (tripDetail.harshBrakesCount) {
        result[driverUuid].harshBrakesCount += tripDetail.harshBrakesCount;
      }
      if (tripDetail.sharpTurnsCount) {
        result[driverUuid].sharpTurnsCount += tripDetail.sharpTurnsCount;
      }
    }
  }

  return result;
};

export const totalScoreByDrivers = (trips: Trips[]) => {
  const weightedAvgEcoDrivingScoresByDriver = {} as Record<
    string,
    TripsDetails[]
  >;

  for (const trip of trips) {
    if (!trip.driver?.uuid) continue;

    const tmp = weightedAvgEcoDrivingScoresByDriver[trip.driver.uuid] ?? [];
    weightedAvgEcoDrivingScoresByDriver[trip.driver.uuid] = [
      ...tmp,
      ...trip.trips,
    ];
  }

  const result = Object.keys(weightedAvgEcoDrivingScoresByDriver)
    .filter((driver) => weightedAvgEcoDrivingScoresByDriver[driver].length > 0)
    .reduce(
      (acc, driver) => {
        return {
          ...acc,
          [driver]: {
            ecoDrivingScore:
              (acc[driver]?.ecoDrivingScore ?? 0) +
              getTripTotalScore(
                weightedAvgEcoDrivingScoresByDriver[driver],
                "ecoDrivingScore",
              ),
            smoothnessScore:
              (acc[driver]?.smoothnessScore ?? 0) +
              getTripTotalScore(
                weightedAvgEcoDrivingScoresByDriver[driver],
                "smoothnessScore",
              ),
          },
        };
      },
      {} as Record<string, EcoDrivingScore>,
    );

  Object.keys(weightedAvgEcoDrivingScoresByDriver).forEach((driver) => {
    if (weightedAvgEcoDrivingScoresByDriver[driver].length > 0) {
      const ecoDrivingTotalDuration = getDriverTripNumber(
        weightedAvgEcoDrivingScoresByDriver[driver],
        "ecoDrivingScore",
      );
      const smoothnessTotalDuration = getDriverTripNumber(
        weightedAvgEcoDrivingScoresByDriver[driver],
        "smoothnessScore",
      );

      result[driver] = {
        ecoDrivingScore:
          ecoDrivingTotalDuration === 0
            ? null
            : (result[driver]?.ecoDrivingScore ?? 0) / ecoDrivingTotalDuration,
        smoothnessScore:
          smoothnessTotalDuration === 0
            ? null
            : (result[driver]?.smoothnessScore ?? 0) / smoothnessTotalDuration,
      };
    }
  });

  return result;
};
